/**
 * Helpers compartilhados da página do catálogo (fatiado de Menu.tsx, #131).
 * #419: chaves por código base (sem região). A moeda de verdade vem do país
 * do tenant (cfg.currency, #413) — este mapa é só fallback de apresentação.
 */
import { normalizeLocale } from '@self-restaurant/shared'

export const LOCALE_CURRENCY: Record<string, string> = {
  pt: 'BRL',
  en: 'USD',
  es: 'EUR',
  fr: 'EUR',
  de: 'EUR',
  it: 'EUR',
  nl: 'EUR',
  zh: 'CNY',
  ja: 'JPY',
  ko: 'KRW',
  ru: 'RUB',
  ar: 'SAR',
  hi: 'INR',
  el: 'EUR',
}

/** Moeda de apresentação do idioma pedido — aceita código regional legado
 * ('pt-BR'…) e casa na base (#419). */
export function currencyFor(locale: string | undefined | null): string {
  return (locale ? LOCALE_CURRENCY[normalizeLocale(locale)] : undefined) ?? 'BRL'
}

export type TranslationRow = { locale: string; name?: string | null; description?: string | null }

/** Linha de tradução como o painel recebe (a API manda translation_status). */
export type SourceTranslationRow = TranslationRow & { translation_status?: string | null }

/**
 * Código BASE do locale, em minúsculas (#403): 'pt-BR' → 'pt'. O Pratzu só
 * cria dado novo com código base — gravações sem linha existente usam isto.
 */
export function baseLocaleOf(locale: string): string {
  const lower = locale.toLowerCase()
  return lower.split('-')[0] ?? lower
}

/**
 * Linha-FONTE para pré-preencher o editor (#387) — cascata sobre o idioma
 * PRIMÁRIO do restaurante, nunca sobre o idioma do painel:
 * 1. linha do locale exato do primário (comparação sem caixa);
 * 2. linha da mesma BASE do primário (ex.: primário pt-BR → pt-PT ou pt),
 *    preferindo `translation_status: 'manual'` e desempate por locale;
 * 3. nada — a edição começa vazia (gravar cria a linha do CÓDIGO BASE).
 * Uma linha de OUTRA base (fr, ar…) nunca volta: nenhum campo pode nascer
 * pré-preenchido com outra língua.
 */
export function sourceTranslation<T extends SourceTranslationRow>(
  translations: T[],
  primary: string,
): T | undefined {
  const norm = (l: string) => l.toLowerCase()
  const primaryNorm = norm(primary)
  const primaryBase = primaryNorm.split('-')[0] ?? primaryNorm
  const exact = translations.find((x) => norm(x.locale) === primaryNorm)
  if (exact) return exact
  const sameBase = translations
    .filter((x) => norm(x.locale).split('-')[0] === primaryBase)
    .sort((a, b) => norm(a.locale).localeCompare(norm(b.locale)))
  return sameBase.find((x) => x.translation_status === 'manual') ?? sameBase[0]
}

/** Melhor tradução para EXIBIÇÃO — mesma cascata do cardápio público:
 *  idioma do painel → base do idioma → primário do restaurante → qualquer uma. */
export function displayTranslation(
  translations: TranslationRow[],
  lang: string,
  primary: string,
): TranslationRow | undefined {
  const base = lang.split('-')[0] ?? lang
  return (
    translations.find((x) => x.locale === lang) ??
    translations.find((x) => x.locale.split('-')[0] === base) ??
    translations.find((x) => x.locale === primary) ??
    translations[0]
  )
}
